/**
 * Time formatting (§2 "Numbers", §12). One formatter per match so every screen agrees.
 *
 * - Real-time matches (timeScale 1): game times are shown as the player's **wall clock**
 *   ("15:30", "Wed 07:00") so "arrives 15:30" tells them when to check back.
 * - Sped-up matches (Quick/Test): wall-clock times would be minutes apart, so the in-world clock
 *   from `@seven-dawns/shared` `clockOf` is used ("Day 4 07:00").
 *
 * Durations are always game minutes, formatted "3 h 20 m".
 */
import {
  clockOf,
  dayOf,
  gameToRealTime,
  MINUTES_PER_DAY,
  type GameTime,
  type MatchConfig,
} from '@seven-dawns/shared';

export interface TimeContext {
  config: Pick<MatchConfig, 'timeScale' | 'durationDays'>;
  startedAtMs: number | null;
}

/** "3 h 20 m", "45 m", "2 h", "0 m". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem} m`;
  if (rem === 0) return `${h} h`;
  return `${h} h ${rem} m`;
}

/** Spoken form for accessible names: "3 hours 20 minutes". */
export function formatDurationLong(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  const hs = h === 1 ? '1 hour' : `${h} hours`;
  const ms = rem === 1 ? '1 minute' : `${rem} minutes`;
  if (h === 0) return ms;
  if (rem === 0) return hs;
  return `${hs} ${ms}`;
}

/** Range in hours: "3–5 h". */
export function formatHourRange(minutes: readonly [number, number]): string {
  const lo = Math.max(0, Math.round(minutes[0] / 60));
  const hi = Math.max(lo, Math.round(minutes[1] / 60));
  if (hi === 0) return 'under 1 h';
  return lo === hi ? `~${hi} h` : `${lo}–${hi} h`;
}

/** Minute of the in-world clock at game time 0 (matches start at 08:00 on day 1). */
const START_MINUTE = 8 * 60;

/**
 * Calendar days between two game times in the clock the player sees: local midnights for
 * real-time matches, in-world midnights for sped-up ones. 0 = same day, 1 = tomorrow.
 */
export function calendarDayOffset(t: GameTime, now: GameTime, ctx: TimeContext): number {
  if (ctx.config.timeScale === 1 && ctx.startedAtMs !== null) {
    const day = (x: GameTime) => {
      const d = new Date(gameToRealTime(x, ctx.startedAtMs ?? 0, ctx.config));
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };
    return Math.round((day(t) - day(now)) / 86_400_000);
  }
  const day = (x: GameTime) => Math.floor((x + START_MINUTE) / MINUTES_PER_DAY);
  return day(t) - day(now);
}

export interface TimeFormat {
  /** Whether times are wall-clock (real-time match) or in-world (sped up). */
  wallClock: boolean;
  clock(t: GameTime): string;
  /** "14:20" today, "tomorrow 06:00", "yesterday 22:10", else "Wed 07:00" / "Day 4 07:00". */
  dayClock(t: GameTime, now: GameTime): string;
  /** "in 1 h 10 m", "1 h ago", "now". */
  relative(t: GameTime, now: GameTime): string;
  /** "15:30 (in 1 h 10 m)". */
  eta(t: GameTime, now: GameTime): string;
  /** "11:20 (3 h ago)". */
  ago(t: GameTime, now: GameTime): string;
  /** "Day 3" or "Wed". */
  dayName(t: GameTime): string;
  /** ISO string for <time datetime>. */
  iso(t: GameTime): string;
  /** Format a local hour of day (0–23), e.g. dispatch hour "08:00". */
  hour(h: number): string;
}

function wallFormatter(options: Intl.DateTimeFormatOptions) {
  try {
    return new Intl.DateTimeFormat(undefined, options);
  } catch {
    return new Intl.DateTimeFormat('en-GB', options);
  }
}

// 24-hour locales get zero-padded hours ("08:48"); 12-hour locales keep "8:48 AM".
const hour12 = wallFormatter({ hour: 'numeric' }).resolvedOptions().hour12 ?? false;
const clockFmt = wallFormatter({ hour: hour12 ? 'numeric' : '2-digit', minute: '2-digit' });
const weekdayFmt = wallFormatter({ weekday: 'short' });

export function createTimeFormat(ctx: TimeContext): TimeFormat {
  const wallClock = ctx.config.timeScale === 1 && ctx.startedAtMs !== null;
  const realMs = (t: GameTime) => gameToRealTime(t, ctx.startedAtMs ?? 0, ctx.config);

  const clock = (t: GameTime) => (wallClock ? clockFmt.format(realMs(t)) : clockOf(t));
  const dayName = (t: GameTime) => (wallClock ? weekdayFmt.format(realMs(t)) : `Day ${dayOf(t)}`);
  const relative = (t: GameTime, now: GameTime) => {
    const d = Math.round(t - now);
    if (Math.abs(d) < 1) return 'now';
    return d > 0 ? `in ${formatDuration(d)}` : `${formatDuration(-d)} ago`;
  };
  const dayClock = (t: GameTime, now: GameTime) => {
    const offset = calendarDayOffset(Math.round(t), Math.round(now), ctx);
    if (offset === 0) return clock(t);
    if (offset === 1) return `tomorrow ${clock(t)}`;
    if (offset === -1) return `yesterday ${clock(t)}`;
    return `${dayName(t)} ${clock(t)}`;
  };
  return {
    wallClock,
    clock,
    dayClock,
    relative,
    dayName,
    eta: (t, now) => `${dayClock(t, now)} (${relative(t, now)})`,
    ago: (t, now) => `${dayClock(t, now)} (${relative(t, now)})`,
    iso: (t) => new Date(wallClock ? realMs(t) : (ctx.startedAtMs ?? 0) + t * 60_000).toISOString(),
    hour: (h) => {
      const d = new Date(2000, 0, 1, ((h % 24) + 24) % 24, 0);
      return clockFmt.format(d);
    },
  };
}

/** Hour of day (0–23) a game time falls on, in the same clock the formatter shows. */
export function hourOfDay(t: GameTime, ctx: TimeContext): number {
  if (ctx.config.timeScale === 1 && ctx.startedAtMs !== null) {
    return new Date(gameToRealTime(t, ctx.startedAtMs, ctx.config)).getHours();
  }
  return Math.floor(((t + START_MINUTE) % MINUTES_PER_DAY) / 60);
}

/** Quiet window state (8 h from `start`). */
export function quietWindow(t: GameTime, start: number | null, ctx: TimeContext) {
  if (start === null) return null;
  const hours = 8;
  const end = (start + hours) % 24;
  const h = hourOfDay(t, ctx);
  const active = (h - start + 24) % 24 < hours;
  return { start, end, active };
}

/** Minutes until the next occurrence of `hour`:00 match-local, for countdowns. */
export function minutesUntilDayTime(now: GameTime, hour: number): number {
  const minuteOfDay = (now + 8 * 60) % MINUTES_PER_DAY;
  const target = hour * 60;
  return (target - minuteOfDay + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}
